import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Eye,
  EyeOff,
  LayoutDashboard,
  List,
  Settings,
  DollarSign,
  Heart,
  ShoppingBag,
  Store,
  LogOut,
  UserCircle,
  User,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useCart } from '../../hooks/useCart';
import { useSupplier } from '../../hooks/useSupplier';
import Button from '../common/Button';
import Input from '../common/Input';
import reviter from '../../assets/reviter.png';

const Header = () => {
  const { user, logout, isAuthenticated, login } = useAuth();
  const { getTotalItems } = useCart();
  const { supplier, isSupplierSite } = useSupplier();
  const navigate = useNavigate();

  const cartItemCount = getTotalItems();

  const [isAuthMenuOpen, setIsAuthMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const authMenuRef = useRef(null);
  const userMenuRef = useRef(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (authMenuRef.current && !authMenuRef.current.contains(event.target)) {
        setIsAuthMenuOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);
      if (result.success) {
        setIsAuthMenuOpen(false);
        setFormData({ email: '', password: '' });
        const role = result.user?.role;
        if (role === 'platform_admin') {
          navigate('/admin-reviter');
        } else if (role === 'store_owner') {
          navigate('/fornecedor/painel');
        } else if (role === 'reseller') {
          navigate('/revendedor/painel');
        } else {
          navigate('/minha-conta');
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Erro ao fazer login. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const toggleAuthMenu = () => {
    setIsAuthMenuOpen(prev => !prev);
    setIsUserMenuOpen(false);
    setError('');
    if (isAuthMenuOpen) {
      setFormData({ email: '', password: '' });
    }
  }

  const toggleUserMenu = () => {
    setIsUserMenuOpen(prev => !prev);
    setIsAuthMenuOpen(false);
  }

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    logout();
  }

  const getBasePath = () => {
    if (isSupplierSite && supplier) {
      return `/fornecedor/${supplier.slug}`;
    }
    return '/';
  };

  const getCartPath = () => {
    if (isSupplierSite && supplier) {
      return `/fornecedor/${supplier.slug}/carrinho`;
    }
    return '/carrinho';
  };

  return (
    <>
      {(isAuthMenuOpen || isUserMenuOpen) && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
          onClick={() => {
            setIsAuthMenuOpen(false);
            setIsUserMenuOpen(false);
          }}
          aria-hidden="true"
        />
      )}

      <header className="bg-white/95 backdrop-blur-sm shadow-lg sticky top-0 z-50 border-b border-gray-100">
        <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-16">
          <div className="flex items-center justify-between h-[60px]">
            <Link to={getBasePath()} className="flex items-center gap-2 group">
              {isSupplierSite && supplier?.name ? (
                <>
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center transform group-hover:scale-110 transition-transform">
                    <span className="text-white font-bold text-xl">
                      {supplier.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <span className="text-2xl font-bold block bg-gradient-to-br from-primary to-secondary bg-clip-text text-transparent">
                      {supplier.name}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className='flex items-end'>
                    <img src={reviter} alt='Logotipo Reviter - Sua Plataforma de Revenda' className='w-max h-10' />
                    <span className="text-2xl font-extrabold bg-gradient-to-br from-primary-dark to-primary bg-clip-text text-transparent tracking-tight">
                      Reviter
                    </span>
                  </div>
                </>
              )}
            </Link>

            <nav className="hidden md:flex items-end pb-3 h-full gap-6 font-medium tracking-tight">
              {isSupplierSite && supplier ? (
                <>
                  <Link
                    to={`/fornecedor/${supplier.slug}`}
                    className="text-app-text hover:text-primary transition-colors relative group"
                  >
                    Início
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all"></span>
                  </Link>
                  <Link
                    to={`/fornecedor/${supplier.slug}/catalogo`}
                    className="text-app-text hover:text-primary transition-colors relative group"
                  >
                    Catálogo Fornecedor
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all"></span>
                  </Link>
                  {/* Loja com programa de revenda mostra link CTA. */}
                  {supplier.businessType === 'reseller' && (
                    <Link
                      to="/quero-ser-revendedor"
                      className="text-primary hover:text-primary-dark font-semibold transition-colors relative group"
                    >
                      Quero ser revendedor
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all"></span>
                    </Link>
                  )}
                </>
              ) : (
                <>
                  {/* Nav institucional do site Reviter — links de marketing pra
                      a LP. Aparecem só pra visitantes (sem tenant resolvido). */}
                  <a
                    href="/#recursos"
                    className="text-app-text font-medium hover:text-primary transition-colors relative group"
                  >
                    Recursos
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all"></span>
                  </a>
                  <a
                    href="/#planos"
                    className="text-app-text font-medium hover:text-primary transition-colors relative group"
                  >
                    Planos
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all"></span>
                  </a>
                  <Link
                    to="/quero-ser-revendedor"
                    className="text-app-text font-medium hover:text-primary transition-colors relative group"
                  >
                    Para revendedores
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all"></span>
                  </Link>
                  {isAuthenticated && user?.role === 'reseller' && (
                    <Link
                      to="/revendedor/painel"
                      className="text-app-text font-medium hover:text-primary transition-colors relative group"
                    >
                      Área do Revendedor
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all"></span>
                    </Link>
                  )}
                </>
              )}
            </nav>

            <div className="flex items-end gap-2">
              <Link
                to={getCartPath()}
                className="relative p-2 text-app-text transition-all rounded-lg hover:bg-primary/5 group"
                aria-label="Sacola"
              >
                {user?.role !== 'store_owner' && (
                  <ShoppingBag className="w-7 h-7 group-hover:scale-110 transition-transform text-primary-dark hover:text-primary" />
                )}
                {cartItemCount > 0 && (
                  <span className="absolute -bottom-0 -right-1 bg-gradient-to-r from-primary to-secondary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold shadow-md ring-2 ring-white">
                    {cartItemCount}
                  </span>
                )}
              </Link>

              {isAuthenticated ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={toggleUserMenu}
                    className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors cursor-pointer"
                    aria-label="Abrir menu do usuário"
                  >
                    <div className="h-8 w-8 bg-gradient-to-br from-primary-dark to-secondary rounded-full flex items-center justify-center text-white text-xl font-bold">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-base font-semibold text-app-text hidden sm:block">
                      {user.name.split(' ')[0]}
                    </span>
                  </button>

                  {isUserMenuOpen && (
                    <div className="absolute right-[-10px] mt-2 w-80 bg-app-surface rounded-lg shadow-xl z-50 border border-gray-100 divide-y divide-gray-100">
                      <div className="p-4">
                        <p className="font-bold text-app-text text-base truncate" title={user.name}>{user.name}</p>
                        <p className="text-sm text-app-text-secondary truncate" title={user.email}>{user.email}</p>
                      </div>

                      <nav className="p-2 space-y-1">
                        {user?.role === 'reseller' && (
                          <Link
                            to="/revendedor/painel"
                            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium text-app-text hover:bg-primary/10 hover:text-primary transition-colors"
                            onClick={() => setIsUserMenuOpen(false)} // Fecha ao clicar
                          >
                            <LayoutDashboard className="w-5 h-5" />
                            <span>Área do Revendedor</span>
                          </Link>
                        )}
                        {user?.role === 'store_owner' && (
                          <Link
                            to="/fornecedor/painel"
                            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium text-app-text hover:bg-primary/10 hover:text-primary transition-colors"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <LayoutDashboard className="w-5 h-5" />
                            <span>Painel de Controle</span>
                          </Link>
                        )}
                        {user?.role === 'reseller' && (
                          <Link
                            to="/revendedor/painel/perfil" // Mude para a rota correta
                            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium text-app-text hover:bg-primary/10 hover:text-primary transition-colors"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <UserCircle className="w-5 h-5" />
                            <span>Gerenciar Perfil</span>
                          </Link>
                        )}
                        {user?.role === 'store_owner' && (
                          <Link
                            to="fornecedor/painel/produtos" // Mude para a rota correta
                            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium text-app-text hover:bg-primary/10 hover:text-primary transition-colors"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <Store className="w-5 h-5" />
                            <span>Gerenciar Produtos</span>
                          </Link>
                        )}
                        {user?.role === 'reseller' && (
                          <Link
                            to="/revendedor/painel/pedidos" // Mude para a rota correta
                            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium text-app-text hover:bg-primary/10 hover:text-primary transition-colors"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <List className="w-5 h-5" />
                            <span>Acompanhar Pedidos</span>
                          </Link>
                        )}
                        {user?.role === 'store_owner' && (
                          <Link
                            to="/fornecedor/painel/pedidos" // Mude para a rota correta
                            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium text-app-text hover:bg-primary/10 hover:text-primary transition-colors"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <List className="w-5 h-5" />
                            <span>Gerenciar Pedidos</span>
                          </Link>
                        )}
                        {user?.role === 'reseller' && (
                          <Link
                            to="/revendedor/painel/pagamentos" // Mude para a rota correta
                            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium text-app-text hover:bg-primary/10 hover:text-primary transition-colors"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <DollarSign className="w-5 h-5" />
                            <span>Central de Pagamentos</span>
                          </Link>
                        )}
                        {user?.role === 'store_owner' && (
                          <Link
                            to="/fornecedor/painel/configuracoes"
                            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium text-app-text hover:bg-primary/10 hover:text-primary transition-colors"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <Settings className="w-5 h-5" />
                            <span>Configurar Vitrine</span>
                          </Link>
                        )}
                        {user?.role === 'client' && (
                          <Link
                            to="/minha-conta"
                            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium text-app-text hover:bg-primary/10 hover:text-primary transition-colors"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <UserCircle className="w-5 h-5" />
                            <span>Minha Conta</span>
                          </Link>
                        )}
                        {user?.role === 'platform_admin' && (
                          <Link
                            to="/admin-reviter"
                            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium text-app-text hover:bg-primary/10 hover:text-primary transition-colors"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <LayoutDashboard className="w-5 h-5" />
                            <span>Admin Reviter</span>
                          </Link>
                        )}
                        {user?.role !== 'store_owner' && (
                          <Link
                            to=""
                            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium text-app-text hover:bg-primary/10 hover:text-primary transition-colors"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <Heart className="w-5 h-5" />
                            <span>Lista de Desejos</span>
                          </Link>
                        )}
                      </nav>

                      <div className="p-2">
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-2 w-full px-3 py-2.5 rounded-md text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <LogOut className="w-5 h-5" />
                          <span>Sair</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="relative" ref={authMenuRef}>
                  <button
                    onClick={toggleAuthMenu}
                    className="relative p-2.5 pb-2 text-app-text hover:text-primary transition-all rounded-lg hover:bg-primary/5 group"
                    aria-label="Abrir menu de login"
                  >
                    <User className="w-6 h-6 group-hover:scale-110 transition-transform text-primary-dark hover:text-primary" />
                  </button>

                  {isAuthMenuOpen && (
                    <div className="absolute right-[-10px] mt-2 w-80 bg-app-surface rounded-lg shadow-xl z-50 border border-gray-100 p-6">
                      <h2 className="text-xl font-extrabold text-center mb-4 text-primary-dark">
                        Acesse o Reviter
                      </h2>

                      {error && (
                        <div className="bg-error/10 border border-error/30 text-error px-3 py-1 rounded-full mb-2 text-sm font-medium text-center">
                          {error}
                        </div>
                      )}

                      {/* Mudei o space-y para 5 para acomodar os labels */}
                      <form onSubmit={handleSubmit} className="space-y-5">

                        {/* --- CAMPO EMAIL COM LABEL MODERNO --- */}
                        <div className="space-y-1.5">
                          <label
                            htmlFor="email-modal-login"
                            className="block text-sm font-medium text-app-text-secondary"
                          >
                            Email
                          </label>
                          <Input
                            id="email-modal-login"
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="seu@email.com"
                          // prop 'label' removida
                          />
                        </div>

                        {/* --- CAMPO SENHA COM LABEL MODERNO E ÍCONE --- */}
                        <div className="space-y-1.5">
                          <label
                            htmlFor="password-modal-login"
                            className="block text-sm font-medium text-app-text-secondary"
                          >
                            Senha
                          </label>
                          {/* 1. Wrapper Relativo */}
                          <div className="relative">
                            <Input
                              id="password-modal-login"
                              type={showPassword ? 'text' : 'password'} // 2. Tipo dinâmico
                              name="password"
                              value={formData.password}
                              onChange={handleChange}
                              required
                              placeholder="••••••••"
                              className="pr-10" // 3. Padding para o ícone
                            // prop 'label' removida
                            />
                            {/* 4. Botão do ícone */}
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute inset-y-0 right-0 pr-3 flex items-center text-app-text-secondary hover:text-app-text transition-colors"
                              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                            >
                              {showPassword ? (
                                <EyeOff className="h-5 w-5" />
                              ) : (
                                <Eye className="h-5 w-5" />
                              )}
                            </button>
                          </div>
                        </div>

                        <Button
                          type="submit"
                          fullWidth
                          disabled={loading}
                        >
                          {loading ? 'Acessando...' : 'Entrar'}
                        </Button>
                      </form>

                      <div className="mt-4 text-center text-sm">
                        <p className="text-app-text-secondary">
                          Não tem uma conta?{' '}
                          <Link
                            to="/cadastrar"
                            className="text-primary font-semibold hover:underline"
                            onClick={() => setIsAuthMenuOpen(false)}
                          >
                            Cadastre-se
                          </Link>
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;